import { useEffect } from 'react';
import { api } from './api/api';
import { refreshLiveBase } from './components/liveBase';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DuelList from './pages/DuelList';
import DuelEdit from './pages/DuelEdit';
import OcgInput from './pages/OcgInput';
import DuelEditorDemo from './pages/DuelEditorDemo';
import DuelTiming from './pages/DuelTiming';
import DeckManagement from './pages/DeckManagement';
import DeckPage from './pages/DeckPage';
import DeckEditor from './pages/DeckEditor';
import DeckLottery from './pages/DeckLottery';
import DeckImages from './pages/DeckImages';
import CardManagement from './pages/CardManagement';
import ExportRequests from './pages/ExportRequests';
import TetherCamDevices from './pages/TetherCamDevices';
import SpeechCheck from './pages/SpeechCheck';
import TetherCamDevice from './pages/TetherCamDevice';
import RecordingSetup from './pages/RecordingSetup';
import LiveSchedule from './pages/LiveSchedule';
import CliLogin from './pages/CliLogin';
import DeviceLogin from './pages/DeviceLogin';
import DiscordActivity from './pages/DiscordActivity';
import { inDiscordActivity } from './utils/discordActivity';
import Nav from './components/Nav';
import AuthGuard from './components/AuthGuard';

function Layout({ children }) {
  // ブラウザが読む補正サーバーの場所（家・会場の Windows の https の口か、同じ origin の /live）を本番に聞き直す（Issue #863）
  useEffect(() => {
    if (!api.checkAuth()) return undefined;
    const ask = () => refreshLiveBase((action, params) => api.project(action, params));
    ask();
    const t = setInterval(ask, 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <Nav />
      <AuthGuard>{children}</AuthGuard>
    </>
  );
}

export default function App() {
  // Discord の Activity の中（`<アプリの ID>.discordsays.com`）では、どの道で開かれても映像のページだけ（Issue #687）
  if (inDiscordActivity()) return <DiscordActivity />;
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/"          element={<Login />} />
        <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
        <Route path="/duels"                                        element={<Layout><DuelList /></Layout>} />
        <Route path="/duels/:duelsId"                               element={<Layout><DuelEdit /></Layout>} />
        <Route path="/duels/:duelsId/game/:gameUuid/input"          element={<Layout><OcgInput /></Layout>} />
        {/* 旧エンジンのシミュレータ（Issue #998 で消した）の古いリンクは入力画面へ */}
        <Route path="/duels/:duelsId/game/:gameUuid/simulator"      element={<Navigate to="../input" relative="path" replace />} />
        {/* 演出の確認（テーマ・演出の一覧・サンプル）とタイミング調節（タイムライン・設定の変化・書き出し）。Issue #643 */}
        <Route path="/duel-editor" element={<Layout><DuelEditorDemo /></Layout>} />
        <Route path="/duel-timing" element={<Layout><DuelTiming /></Layout>} />
        {/* 描く側の演出ページ（ゲームサーバーの観戦クライアントが開く）。本番の API を呼ばないので Nav もログインも無し。Issue #766 */}
        {/* Discord の Activity と同じ映像のページ（Discord の外での確かめ用。ログイン無し。Issue #687） */}
        <Route path="/discord-activity" element={<DiscordActivity />} />
        <Route path="/decks"     element={<Layout><DeckManagement /></Layout>} />
        <Route path="/decks/check" element={<Layout><DeckManagement tab="check" /></Layout>} />
        <Route path="/decks/images" element={<Layout><DeckImages /></Layout>} />
        <Route path="/decks/:deckId" element={<Layout><DeckPage /></Layout>} />
        <Route path="/decks/:deckId/edit" element={<Layout><DeckEditor /></Layout>} />
        <Route path="/lottery"   element={<Layout><DeckLottery /></Layout>} />
        <Route path="/cards"     element={<Navigate to="/cards/import" replace />} />
        <Route path="/cards/:tab" element={<Layout><CardManagement /></Layout>} />
        <Route path="/exports"   element={<Navigate to="/exports/all" replace />} />
        <Route path="/exports/:tab" element={<Layout><ExportRequests /></Layout>} />
        <Route path="/cli-login" element={<Layout><CliLogin /></Layout>} />
        <Route path="/device-login" element={<Layout><DeviceLogin /></Layout>} />
        <Route path="/shoot" element={<Navigate to="/shoot/schedule" replace />} />
        <Route path="/shoot/schedule" element={<Layout><LiveSchedule /></Layout>} />
        <Route path="/shoot/recording" element={<Layout><RecordingSetup /></Layout>} />
        <Route path="/shoot/devices" element={<Layout><TetherCamDevices /></Layout>} />
        <Route path="/shoot/devices/:name" element={<Layout><TetherCamDevice /></Layout>} />
        <Route path="/shoot/speech" element={<Layout><SpeechCheck /></Layout>} />
        {/* 撮影の設定（/shoot/setup）は Issue #801 で消した。古い URL は TetherCam の一覧へ */}
        {['/shoot/setup', '/shoot/obs', '/shoot/tethercam', '/shoot-setup', '/tethercam-setup'].map((p) => (
          <Route key={p} path={p} element={<Navigate to="/shoot/devices" replace />} />
        ))}
        <Route path="*"          element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
